import React, { useState, useEffect } from 'react';
import { useParams, Link } from 'react-router-dom';
import styled from 'styled-components';
import { motion } from 'framer-motion';
import { FaGithub, FaExternalLinkAlt, FaArrowLeft } from 'react-icons/fa';

// Components
import PageHeader from '../components/ui/PageHeader';
import PageTransition from '../components/ui/PageTransition';
import portfolioProject from '../assets/images/portfolio-project.svg';
import businessConsulting from '../assets/images/business-consulting.svg';
import sortingVisualizer from '../assets/images/sorting-visualizer.svg';
import dsms from '../assets/images/dsms.svg';
import projectDetail1 from '../assets/images/project-detail-1.svg';
import projectDetail2 from '../assets/images/project-detail-2.svg';
import projectDetail3 from '../assets/images/project-detail-3.svg';

const ProjectDetail = () => {
  const { id } = useParams();
  const [project, setProject] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    const fetchProject = async () => {
      try {
        setLoading(true);
        // In a real app, this would be an API call
        // const response = await fetch(`/api/projects/${id}`);
        // const data = await response.json();
        // setProject(data.data);

        // For now, using sample data
        const sampleProjects = [
          {
            id: 1,
            title: 'My Own Portfolio',
            description: 'A personal portfolio website showcasing my skills, projects, and experience',
            fullDescription: 'This portfolio website showcases my creative work with a modern, responsive design and smooth animations. Built with React and Styled Components, it features a clean, minimalist aesthetic that puts the focus on the content while providing an engaging user experience through thoughtful interactions and transitions.',
            image: portfolioProject,
            tags: ['React', 'Styled Components', 'Framer Motion'],
            demoLink: 'https://prince2004.vercel.app/',
            githubLink: 'https://github.com/princeyadavpy',
            category: 'Web Development',
            features: [
              'Responsive design for all devices',
              'Dark/light mode toggle',
              'Smooth page transitions',
              'Project filtering and categorization',
              'Contact form with validation',
              'Performance optimized images and assets',
              'SEO friendly structure'
            ],
            technologies: [
              { name: 'React', description: 'Frontend UI library' },
              { name: 'Styled Components', description: 'CSS-in-JS styling' },
              { name: 'Framer Motion', description: 'Animation library' },
              { name: 'React Router', description: 'Client-side routing' },
              { name: 'Formik', description: 'Form handling' },
              { name: 'Netlify', description: 'Hosting and deployment' }
            ],
            images: [
              projectDetail1,
              projectDetail2,
              projectDetail3
            ]
          },
          {
            id: 2,
            title: 'Business Consulting Website',
            description: 'Website for Flash Dynamics Syndicate offering business consulting and services',
            fullDescription: 'This comprehensive e-commerce solution provides businesses with a scalable and customizable platform to sell products online. Built with React for the frontend, Node.js for the backend, and MongoDB for the database, this application offers a seamless shopping experience for customers and powerful management tools for administrators.',
            image: businessConsulting,
            tags: ['HTML/CSS', 'JavaScript', 'Responsive Design'],
            demoLink: 'https://fdsyd.com/',
            githubLink: 'https://github.com/princeyadavpy',
            category: 'Web Development',
            features: [
              'User authentication and authorization',
              'Product catalog with search and filtering',
              'Shopping cart and checkout process',
              'Payment gateway integration',
              'Order management system',
              'Admin dashboard for inventory management',
              'Responsive design for all devices'
            ],
            technologies: [
              { name: 'React', description: 'Frontend UI library' },
              { name: 'Redux', description: 'State management' },
              { name: 'Node.js', description: 'Backend runtime' },
              { name: 'Express', description: 'Web framework' },
              { name: 'MongoDB', description: 'NoSQL database' },
              { name: 'Stripe', description: 'Payment processing' },
              { name: 'AWS S3', description: 'Image storage' }
            ],
            images: [
              projectDetail1,
              projectDetail2,
              projectDetail3
            ]
          },
          {
            id: 3,
            title: 'Sorting Visualizer and Comparison Tool',
            description: 'A web application to visually demonstrate sorting algorithms and compare their performance',
            fullDescription: 'This task management application helps users organize their work with intuitive drag-and-drop functionality. Built with React and Redux, it allows users to create, categorize, and prioritize tasks across different boards, making project management simple and visual.',
            image: sortingVisualizer,
            tags: ['C++', 'Algorithms', 'Data Structures'],
            demoLink: '#',
            githubLink: 'https://github.com/princeyadavpy/All-Sorting-Visualizer-and-Comparison-Tool',
            category: 'Algorithms',
            features: [
              'Drag-and-drop task management',
              'Multiple project boards',
              'Task prioritization and categorization',
              'Due date and reminder setting',
              'User authentication and data persistence',
              'Collaborative features for team projects',
              'Mobile-friendly interface'
            ],
            technologies: [
              { name: 'React', description: 'Frontend UI library' },
              { name: 'Redux', description: 'State management' },
              { name: 'Firebase', description: 'Backend and authentication' },
              { name: 'React Beautiful DnD', description: 'Drag-and-drop functionality' },
              { name: 'Material UI', description: 'Component library' },
              { name: 'Date-fns', description: 'Date manipulation' }
            ],
            images: [
              projectDetail1,
              projectDetail2,
              projectDetail3
            ]
          },
          {
            id: 4,
            title: 'Data Structure Operations Management System',
            description: 'A comprehensive system implemented in C++ that integrates various data structures and algorithms',
            fullDescription: 'This mobile banking application provides users with a secure and convenient way to manage their finances on the go. Built with React Native and integrated with secure authentication methods, it offers a comprehensive suite of banking features while maintaining the highest standards of security and user privacy.',
            image: dsms,
            tags: ['C++', 'Data Structures', 'Algorithms'],
            demoLink: '#',
            githubLink: 'https://github.com/princeyadavpy/DSA-Project-Data-Structure-Operations-Management-System',
            category: 'Data Structures',
            features: [
              'Biometric authentication (fingerprint/face recognition)',
              'Account balance and transaction history',
              'Fund transfers and bill payments',
              'Mobile check deposit',
              'Budget tracking and financial insights',
              'Notification system for account activities',
              'Localized ATM and branch finder'
            ],
            technologies: [
              { name: 'React Native', description: 'Cross-platform mobile framework' },
              { name: 'Node.js', description: 'Backend runtime' },
              { name: 'MongoDB', description: 'NoSQL database' },
              { name: 'JWT', description: 'Authentication' },
              { name: 'Expo', description: 'Development toolkit' },
              { name: 'React Navigation', description: 'Navigation library' },
              { name: 'Redux', description: 'State management' }
            ],
            images: [
              projectDetail1,
              projectDetail2,
              projectDetail3
            ]
          }
        ];

        const foundProject = sampleProjects.find(p => p.id === parseInt(id));

        if (!foundProject) {
          throw new Error('Project not found');
        }

        setProject(foundProject);
        setLoading(false);
      } catch (error) {
        setError('Project not found or failed to load.');
        setLoading(false);
      }
    };

    fetchProject();
  }, [id]);

  if (loading) return <LoadingContainer>Loading project details...</LoadingContainer>;
  if (error) return <ErrorContainer>{error}</ErrorContainer>;
  if (!project) return <ErrorContainer>Project not found</ErrorContainer>;

  return (
    <PageTransition>
      <ProjectDetailContainer
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        exit={{ opacity: 0 }}
        transition={{ duration: 0.5 }}
      >
        <BackLink to="/projects">
          <FaArrowLeft /> Back to Projects
        </BackLink>

        <PageHeader
          title={project.title}
          subtitle={Array.isArray(project.technologies)
            ? project.technologies.map(t => typeof t === 'string' ? t : t.name).join(' • ')
            : ''}
        />

        <ProjectContent>
          <ProjectImageContainer>
            <ProjectImage src={project.image} alt={project.title} />
          </ProjectImageContainer>

          <ProjectInfo>
            <ProjectDescription dangerouslySetInnerHTML={{ __html: project.fullDescription || project.description }} />

            <TechnologiesContainer>
              <SectionTitle>Technologies Used</SectionTitle>
              <TechnologiesList>
                {project.technologies.map((tech, index) => (
                  <TechnologyTag key={index}>{tech}</TechnologyTag>
                ))}
              </TechnologiesList>
            </TechnologiesContainer>

            <ProjectLinks>
              {project.githubLink && (
                <ProjectLink href={project.githubLink} target="_blank" rel="noopener noreferrer">
                  <FaGithub /> View Code
                </ProjectLink>
              )}
              {project.demoLink && (
                <ProjectLink href={project.demoLink} target="_blank" rel="noopener noreferrer" primary>
                  <FaExternalLinkAlt /> Live Demo
                </ProjectLink>
              )}
            </ProjectLinks>
          </ProjectInfo>
        </ProjectContent>
      </ProjectDetailContainer>
    </PageTransition>
  );
};

const ProjectDetailContainer = styled(motion.div)`
  padding: 2rem;
  max-width: 1200px;
  margin: 0 auto;
  
  @media (max-width: 768px) {
    padding: 1rem;
  }
`;

const BackLink = styled(Link)`
  display: inline-flex;
  align-items: center;
  margin-bottom: 2rem;
  color: var(--text-color);
  text-decoration: none;
  font-weight: 500;
  transition: color 0.3s ease;
  
  svg {
    margin-right: 0.5rem;
  }
  
  &:hover {
    color: var(--primary-color);
  }
`;

const ProjectContent = styled.div`
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 3rem;
  margin-top: 2rem;
  
  @media (max-width: 992px) {
    grid-template-columns: 1fr;
  }
`;

const ProjectImageContainer = styled.div`
  border-radius: 8px;
  overflow: hidden;
  box-shadow: 0 5px 15px rgba(0, 0, 0, 0.1);
`;

const ProjectImage = styled.img`
  width: 100%;
  height: auto;
  display: block;
  transition: transform 0.3s ease;
  
  &:hover {
    transform: scale(1.02);
  }
`;

const ProjectInfo = styled.div`
  display: flex;
  flex-direction: column;
`;

const ProjectDescription = styled.div`
  margin-bottom: 2rem;
  line-height: 1.8;
  color: var(--text-color);
  
  p {
    margin-bottom: 1rem;
  }
  
  ul, ol {
    margin-left: 1.5rem;
    margin-bottom: 1rem;
  }
`;

const SectionTitle = styled.h3`
  font-size: 1.2rem;
  margin-bottom: 1rem;
  color: var(--text-color);
`;

const TechnologiesContainer = styled.div`
  margin-bottom: 2rem;
`;

const TechnologiesList = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 0.5rem;
`;

const TechnologyTag = styled.span`
  background-color: var(--bg-secondary);
  color: var(--text-color);
  padding: 0.5rem 1rem;
  border-radius: 30px;
  font-size: 0.9rem;
  font-weight: 500;
`;

const ProjectLinks = styled.div`
  display: flex;
  gap: 1rem;
  margin-top: auto;
  
  @media (max-width: 576px) {
    flex-direction: column;
  }
`;

const ProjectLink = styled.a`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  padding: 0.8rem 1.5rem;
  border-radius: 4px;
  text-decoration: none;
  font-weight: 500;
  transition: all 0.3s ease;
  background-color: ${props => props.primary ? 'var(--primary-color)' : 'var(--bg-secondary)'};
  color: ${props => props.primary ? '#fff' : 'var(--text-color)'};
  
  svg {
    margin-right: 0.5rem;
  }
  
  &:hover {
    transform: translateY(-2px);
    box-shadow: 0 5px 15px rgba(0, 0, 0, 0.1);
    background-color: ${props => props.primary ? 'var(--primary-dark)' : 'var(--bg-tertiary)'};
  }
`;

const LoadingContainer = styled.div`
  display: flex;
  justify-content: center;
  align-items: center;
  height: 50vh;
  font-size: 1.2rem;
`;

const ErrorContainer = styled.div`
  display: flex;
  justify-content: center;
  align-items: center;
  height: 50vh;
  color: #e74c3c;
  font-size: 1.2rem;
`;

export default ProjectDetail;